export const WORKSPACE_ROLES = {
  ADMIN: 'admin',
  MEMBER: 'member',
  GUEST: 'guest',
} as const

export type WorkspaceRole = (typeof WORKSPACE_ROLES)[keyof typeof WORKSPACE_ROLES]

export const WORKSPACE_PERMISSIONS = {
  MANAGE_MEMBERS: 'manage_members',
  INVITE_MEMBERS: 'invite_members',
  REMOVE_MEMBERS: 'remove_members',
  CHANGE_ROLES: 'change_roles',
  MANAGE_ROLES: 'manage_roles',
  MANAGE_WORKSPACE_SETTINGS: 'manage_workspace_settings',
  MANAGE_SUBSCRIPTION: 'manage_subscription',
  CREATE_TASKS: 'create_tasks',
  EDIT_TASKS: 'edit_tasks',
  DELETE_TASKS: 'delete_tasks',
  VIEW_TASKS: 'view_tasks',
  MANAGE_LABELS: 'manage_labels',
  MANAGE_STATUSES: 'manage_statuses',
  // AI feature gates — mirror the backend's WorkspacePermissions constants.
  // USE_AI is the per-user gate for /ai/search, /ai/ask, /ai/extract-tasks.
  // MANAGE_AI_SETTINGS gates the admin surface: kill-switch toggle, reindex,
  // jobs panel, embedding debug, /ai/admin/usage. Default Admin role gets both;
  // Member gets USE_AI only.
  USE_AI: 'use_ai',
  MANAGE_AI_SETTINGS: 'manage_ai_settings',
} as const

export type WorkspacePermission = (typeof WORKSPACE_PERMISSIONS)[keyof typeof WORKSPACE_PERMISSIONS]

export const ROLE_PERMISSIONS: Record<WorkspaceRole, Set<WorkspacePermission>> = {
  // Admin picks up MANAGE_AI_SETTINGS + USE_AI automatically via Object.values.
  admin: new Set(Object.values(WORKSPACE_PERMISSIONS)),
  member: new Set([
    WORKSPACE_PERMISSIONS.CREATE_TASKS,
    WORKSPACE_PERMISSIONS.EDIT_TASKS,
    WORKSPACE_PERMISSIONS.DELETE_TASKS,
    WORKSPACE_PERMISSIONS.VIEW_TASKS,
    WORKSPACE_PERMISSIONS.MANAGE_LABELS,
    // Use AI but cannot manage settings/reindex — that's admin-only.
    WORKSPACE_PERMISSIONS.USE_AI,
  ]),
  guest: new Set([WORKSPACE_PERMISSIONS.VIEW_TASKS]),
}

export function hasPermission(role: WorkspaceRole, permission: WorkspacePermission): boolean {
  return ROLE_PERMISSIONS[role]?.has(permission) ?? false
}

export const ROLE_DISPLAY: Record<WorkspaceRole, { label: string; description: string }> = {
  admin: { label: 'Admin', description: 'Full workspace management access' },
  member: { label: 'Member', description: 'Can create and manage tasks' },
  guest: { label: 'Guest', description: 'View-only access' },
}

export const PERMISSION_GROUPS = [
  { label: 'Workspace', permissions: ['manage_workspace_settings', 'manage_subscription', 'manage_roles'] },
  { label: 'Members', permissions: ['manage_members', 'invite_members', 'remove_members', 'change_roles'] },
  { label: 'Tasks', permissions: ['create_tasks', 'edit_tasks', 'delete_tasks', 'view_tasks'] },
  { label: 'Organization', permissions: ['manage_labels', 'manage_statuses'] },
  { label: 'AI', permissions: ['use_ai', 'manage_ai_settings'] },
]
